import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { MathTextBlock } from '../../components/MathTextBlock';
import { buildPapDetail } from '../../features/ttqib/papDisplay';
import type { PrivateActivityPackage } from '../../services/ttqService';
import {
  factoryService,
  type FactoryBatch,
  type FactoryCriticalZoneItem,
  type FactoryStructuralUnit,
  type FactoryTtqAdmissionResult,
  type FactoryTtqProduct,
  type FactoryWarehouseTtqa,
  type FactoryWarehouseTtqaParticle,
} from '../../services/factoryService';
import { structuralUnitExtractQa } from '../utils/extractQaPreview';
import { resolveAdmissionParticleQa } from '../utils/resolveAdmissionParticleQa';
import { truncateMathAwarePreview } from '../utils/truncateMathAwarePreview';
import {
  criticalIngressView,
  criticalParticleIds,
  criticalWarehouseTtqaId,
  FACTORY_WAREHOUSE_STATUS_LABEL,
} from '../utils/ttqProductCriticalView';

type ProductStats = {
  created: number;
  joined: number;
  critical: number;
  productCount: number;
  groupCount: number;
};

type ParticleDetail = {
  groupTitle: string;
  question: string;
  answer: string;
  subQuestions: string[];
  subAnswers: string[];
  pap: Record<string, unknown> | null;
  pendingItemId?: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function dispositionShort(d: string | null | undefined): string {
  if (d === 'joined') return '已加入';
  if (d === 'created') return '新建';
  if (d === 'critical') return '有溢出';
  return '未入仓';
}

function lookupExtractQa(
  particleId: string | null | undefined,
  units: FactoryStructuralUnit[],
) {
  const id = String(particleId || '');
  const colon = id.lastIndexOf(':');
  if (colon <= 0) return null;
  const unitId = id.slice(0, colon);
  const qi = Number(id.slice(colon + 1));
  if (!Number.isFinite(qi)) return null;
  const unit = units.find((u) => u.id === unitId);
  if (!unit) return null;
  return structuralUnitExtractQa(unit)[qi] || null;
}

function detailFromWarehouseParticle(
  groupTitle: string,
  p: FactoryWarehouseTtqaParticle,
  units: FactoryStructuralUnit[],
): ParticleDetail {
  const fromUnit = lookupExtractQa(p.particleId, units);
  const qa = resolveAdmissionParticleQa({
    particleQuestions: p.particleQuestions,
    particleAnswers: p.particleAnswers,
    particleTitle: p.particleTitle,
    subQuestions: p.subQuestions,
    subAnswers: p.subAnswers,
    extractQuestion: fromUnit?.question,
    extractAnswer: fromUnit?.answer,
    extractSubQuestions: fromUnit?.subQuestions,
    extractSubAnswers: fromUnit?.subAnswers,
    pap: p.pap,
  });
  return {
    groupTitle,
    question: qa.question || String(p.particleId || ''),
    answer: qa.answer,
    subQuestions: qa.subQuestions,
    subAnswers: qa.subAnswers,
    pap: (p.pap || null) as Record<string, unknown> | null,
  };
}

function detailFromCriticalItem(
  item: FactoryCriticalZoneItem,
  productsById: Map<string, FactoryTtqProduct>,
  units: FactoryStructuralUnit[],
): ParticleDetail {
  const view = criticalIngressView(item, productsById);
  const candidate = asRecord(item.candidate) || {};
  const members = Array.isArray(candidate.members) ? candidate.members : [];
  const first = asRecord(members[0]) || {};
  const snap = asRecord(first.snapshot) || first;
  const particleId = String(first.particleId || snap.particleId || '').trim();
  const fromUnit = lookupExtractQa(particleId, units);
  const qa = resolveAdmissionParticleQa({
    particleQuestions: String(snap.particleQuestions || snap.particleTitle || ''),
    particleAnswers: String(snap.particleAnswers || snap.answer || ''),
    subQuestions: Array.isArray(snap.subQuestions)
      ? snap.subQuestions.map((s) => String(s || '').trim()).filter(Boolean)
      : [],
    subAnswers: Array.isArray(snap.subAnswers)
      ? snap.subAnswers.map((s) => String(s || '').trim()).filter(Boolean)
      : [],
    extractQuestion: fromUnit?.question,
    extractAnswer: fromUnit?.answer,
    extractSubQuestions: fromUnit?.subQuestions,
    extractSubAnswers: fromUnit?.subAnswers,
    pap: asRecord(snap.pap),
  });
  return {
    groupTitle: view.groupTitle,
    question: qa.question || view.particleLines[0] || view.groupTitle,
    answer: qa.answer,
    subQuestions: qa.subQuestions,
    subAnswers: qa.subAnswers,
    pap: (asRecord(snap.pap) || null) as Record<string, unknown> | null,
    pendingItemId: item.id,
  };
}

function ParticleDetailDialog(props: {
  detail: ParticleDetail;
  onClose: () => void;
}) {
  const { detail, onClose } = props;
  const papDetail = buildPapDetail((detail.pap || null) as PrivateActivityPackage | null);
  const bright = { color: '#FFFFFF' as const };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="知识颗粒详情"
        className="ttq-particle-dialog max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface p-5 shadow-xl"
        style={bright}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs" style={{ color: '#E8ECF1' }}>{detail.groupTitle}</p>
            <h3 className="mt-1 text-base font-semibold" style={bright}>知识颗粒</h3>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary"
            style={{ color: '#FFFFFF' }}
            onClick={onClose}
          >
            关闭
          </button>
        </div>
        <section className="mt-4">
          <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>题干</h4>
          <div className="mt-2 text-sm" style={bright}>
            {detail.question
              ? <MathTextBlock text={detail.question} className="text-sm" />
              : <span style={{ color: '#E8ECF1' }}>暂无题干</span>}
          </div>
          {detail.subQuestions.length > 0 ? (
            <div className="mt-3 space-y-2">
              {detail.subQuestions.map((subQ, sqi) => (
                <div key={`subq-${sqi}`} className="flex gap-2 text-sm" style={bright}>
                  <span className="shrink-0 tabular-nums opacity-80">({sqi + 1})</span>
                  <MathTextBlock text={subQ} className="text-sm" />
                </div>
              ))}
            </div>
          ) : null}
        </section>
        <section className="mt-4 border-t border-border pt-4">
          <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>答案</h4>
          <div className="mt-2 text-sm" style={bright}>
            {detail.answer
              ? <MathTextBlock text={detail.answer} className="text-sm" />
              : <span style={{ color: '#E8ECF1' }}>暂无答案</span>}
          </div>
          {detail.subAnswers.length > 0 ? (
            <div className="mt-3 space-y-2">
              {detail.subAnswers.map((subA, sai) => (
                <div key={`suba-${sai}`} className="flex gap-2 text-sm" style={bright}>
                  <span className="shrink-0 tabular-nums opacity-80">({sai + 1})</span>
                  <MathTextBlock text={subA} className="text-sm" />
                </div>
              ))}
            </div>
          ) : null}
        </section>
        {papDetail.hasContent ? (
          <section className="mt-4 border-t border-border pt-4">
            <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>隐性能力</h4>
            <ol className="mt-2 space-y-3 text-sm" style={bright}>
              {papDetail.implicitSteps.map((step, index) => (
                <li key={`${step.implicitAbility}-${index}`}>
                  <div className="font-medium">{index + 1}. {step.implicitAbility}</div>
                  {step.actionObject ? (
                    <div className="mt-1 text-xs opacity-80">作用对象：{step.actionObject}</div>
                  ) : null}
                </li>
              ))}
            </ol>
          </section>
        ) : null}
      </div>
    </div>
  );
}

/**
 * 任务「成品智能体」拓扑：大方块=推荐组；小方块=已在组内颗粒；同组末尾高亮块=待审。
 * 已入库 / 待审：点方块看颗粒详情；待审另有 checkbox 多选入库。
 */
export function TaskProductsOpsPanel(props: {
  batchId: string;
  batch: FactoryBatch | null;
  admission: FactoryTtqAdmissionResult | null;
  productStats: ProductStats;
  resolveBusy: boolean;
  needsResolve: boolean;
  jobProgress: string;
  onAdmissionChange: (next: FactoryTtqAdmissionResult) => void;
  onToast: (message: string, durationMs?: number) => void;
}) {
  const {
    batchId,
    batch,
    admission,
    productStats,
    resolveBusy,
    needsResolve,
    jobProgress,
    onAdmissionChange,
    onToast,
  } = props;

  const [products, setProducts] = useState<FactoryTtqProduct[]>([]);
  const [critical, setCritical] = useState<FactoryCriticalZoneItem[]>([]);
  const [structuralUnits, setStructuralUnits] = useState<FactoryStructuralUnit[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [booted, setBooted] = useState(false);
  const [selectedPendingIds, setSelectedPendingIds] = useState<Set<string>>(() => new Set());
  const [particleDetail, setParticleDetail] = useState<ParticleDetail | null>(null);

  const rows = admission?.warehouseTtqas || [];

  const linkedKey = useMemo(() => {
    const ids = rows
      .map((r) => String(r.factoryMeta?.productId || '').trim())
      .filter(Boolean)
      .sort();
    return `${batchId}|${ids.join(',')}|${admission?.builtAt || ''}`;
  }, [admission?.builtAt, batchId, rows]);

  const reloadExtras = useCallback(async () => {
    const linkedIds = new Set(
      linkedKey.split('|')[1]?.split(',').filter(Boolean) || [],
    );
    const [allProducts, allCritical, structs] = await Promise.all([
      factoryService.listTtqProducts(),
      factoryService.listTtqCriticalZone({ status: 'pending' }),
      factoryService.listStructuralUnits(batchId).catch(() => [] as FactoryStructuralUnit[]),
    ]);
    setProducts(
      allProducts.filter((p) => p.sourceBatchId === batchId || linkedIds.has(p.id)),
    );
    setCritical(allCritical.filter((it) => it.batchId === batchId));
    setStructuralUnits(structs);
  }, [batchId, linkedKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 成品智能体已新建/加入时，清掉历史 over_cap 残留待审
        await factoryService.cleanupStaleTtqCritical(batchId).catch(() => null);
        if (cancelled) return;
        await reloadExtras();
      } catch {
        /* soft */
      } finally {
        if (!cancelled) setBooted(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId, reloadExtras]);

  const productsById = useMemo(() => {
    const map = new Map<string, FactoryTtqProduct>();
    for (const p of products) map.set(p.id, p);
    return map;
  }, [products]);

  const settledGroupIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of rows) {
      const d = r.factoryMeta?.disposition;
      if ((d === 'created' || d === 'joined') && r.factoryMeta?.productId) {
        ids.add(r.id);
      }
    }
    return ids;
  }, [rows]);

  const { criticalByGroupId, orphanCritical } = useMemo(() => {
    const byGroup = new Map<string, FactoryCriticalZoneItem[]>();
    const orphans: FactoryCriticalZoneItem[] = [];
    const rowIds = new Set(rows.map((r) => r.id));
    const titleToId = new Map<string, string>();
    for (const r of rows) {
      const t = String(r.title || '').trim();
      if (t) titleToId.set(t, r.id);
    }
    for (const item of critical) {
      const wid = criticalWarehouseTtqaId(item);
      const title = String(asRecord(item.candidate)?.title || '').trim();
      const groupId = (wid && rowIds.has(wid) ? wid : null)
        || (title ? titleToId.get(title) : null)
        || null;
      // 已新建/加入的推荐组：历史待审视为过期，不展示
      if (groupId && settledGroupIds.has(groupId)) continue;
      if (groupId) {
        const list = byGroup.get(groupId) || [];
        list.push(item);
        byGroup.set(groupId, list);
      } else {
        orphans.push(item);
      }
    }
    return { criticalByGroupId: byGroup, orphanCritical: orphans };
  }, [critical, rows, settledGroupIds]);

  const togglePending = (id: string) => {
    setSelectedPendingIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onDecideMany = async (ids: string[], decision: 'approve' | 'reject') => {
    if (!ids.length) return;
    setBusyId('bulk');
    try {
      const res = await factoryService.decideTtqCriticalZoneBulk(ids, decision);
      if (res.ttqAdmission) onAdmissionChange(res.ttqAdmission);
      else if (res.ok > 0) {
        const adm = await factoryService.getTtqAdmission(batchId).catch(() => null);
        if (adm?.ttqAdmission) onAdmissionChange(adm.ttqAdmission);
      }
      if (res.ok > 0 && !(res.errors?.length)) {
        onToast(res.message || (decision === 'approve' ? `已入库 ${res.ok} 条` : `已驳回 ${res.ok} 条`), 2400);
      } else if (res.ok > 0) {
        onToast(
          `${res.message || ''}；部分失败：${res.errors[0]?.error || ''}`.trim(),
          4800,
        );
      } else {
        onToast(res.errors?.[0]?.error || '操作失败', 4800);
      }
      setSelectedPendingIds(new Set());
      await reloadExtras();
    } catch (err) {
      onToast(err instanceof Error ? err.message : String(err), 4500);
    } finally {
      setBusyId(null);
    }
  };

  const onResolve = async () => {
    setBusyId('resolve');
    try {
      const res = await factoryService.resolveTtqProducts(batchId);
      if (res.ttqAdmission) onAdmissionChange(res.ttqAdmission);
      onToast('已写入成品智能体仓', 2400);
      await reloadExtras();
    } catch (err) {
      onToast(err instanceof Error ? err.message : String(err), 4500);
    } finally {
      setBusyId(null);
    }
  };

  const onWarehouse = async (warehouseStatus: string) => {
    setBusyId('warehouse');
    try {
      await factoryService.updateWarehouseStatus(batchId, warehouseStatus);
      onToast(
        `货架：${FACTORY_WAREHOUSE_STATUS_LABEL[warehouseStatus] || warehouseStatus}`,
        2400,
      );
    } catch (err) {
      onToast(err instanceof Error ? err.message : String(err), 4500);
    } finally {
      setBusyId(null);
    }
  };

  const onPatchCap = async (productId: string, memberCap: number) => {
    setBusyId(productId);
    try {
      const res = await factoryService.patchTtqProduct(productId, { memberCap });
      onToast(res.message || `上限 ${memberCap}`, 2400);
      await reloadExtras();
    } catch (err) {
      onToast(err instanceof Error ? err.message : String(err), 4500);
    } finally {
      setBusyId(null);
    }
  };

  const bulkBusy = busyId === 'bulk';

  const selectPendingIds = (ids: string[], mode: 'all' | 'none' | 'only') => {
    setSelectedPendingIds((prev) => {
      if (mode === 'only') return new Set(ids);
      const next = new Set(prev);
      if (mode === 'all') {
        for (const id of ids) next.add(id);
      } else {
        for (const id of ids) next.delete(id);
      }
      return next;
    });
  };

  const renderGroupToolbar = (opts: {
    pendingItems: FactoryCriticalZoneItem[];
    productId?: string | null;
    memberCap?: number | null;
    capBusy?: boolean;
  }) => {
    const pendingIds = opts.pendingItems.map((it) => it.id);
    const selectedInGroup = pendingIds.filter((id) => selectedPendingIds.has(id));
    const selectedN = selectedInGroup.length;
    const hasPending = pendingIds.length > 0;
    return (
      <div className="factory-product-topo-group-actions">
        {opts.productId ? (
          <label className="factory-product-topo-cap">
            上限
            <input
              type="number"
              min={1}
              max={200}
              defaultValue={opts.memberCap || 20}
              key={`${opts.productId}-${opts.memberCap || 20}`}
              disabled={opts.capBusy}
              onBlur={(e) => {
                const next = Math.max(1, Math.min(200, Math.floor(Number(e.target.value) || 20)));
                const prev = Number(opts.memberCap) || 20;
                if (opts.productId && next !== prev) void onPatchCap(opts.productId, next);
              }}
            />
          </label>
        ) : null}
        {hasPending ? (
          <span className="factory-product-topo-bulk-actions">
            <button
              type="button"
              disabled={bulkBusy || selectedN === pendingIds.length}
              onClick={() => selectPendingIds(pendingIds, 'all')}
            >
              全选
            </button>
            <button
              type="button"
              disabled={bulkBusy || selectedN === 0}
              onClick={() => selectPendingIds(pendingIds, 'none')}
            >
              全不选
            </button>
            <button
              type="button"
              data-approve="1"
              disabled={bulkBusy || pendingIds.length === 0}
              onClick={() => {
                const ids = selectedN > 0 ? selectedInGroup : pendingIds;
                void onDecideMany(ids, 'approve');
              }}
              title={selectedN > 0 ? `入库已选 ${selectedN}` : `全选并入库 ${pendingIds.length}`}
            >
              {bulkBusy ? '入库中…' : selectedN > 0 ? '入库' : '全部入库'}
            </button>
            {selectedN > 0 ? (
              <button
                type="button"
                disabled={bulkBusy}
                onClick={() => void onDecideMany(selectedInGroup, 'reject')}
              >
                驳回
              </button>
            ) : null}
          </span>
        ) : null}
      </div>
    );
  };

  const renderPendingChip = (item: FactoryCriticalZoneItem) => {
    const view = criticalIngressView(item, productsById);
    const detail = detailFromCriticalItem(item, productsById, structuralUnits);
    const stem = truncateMathAwarePreview(detail.question || view.groupTitle, 36);
    const checked = selectedPendingIds.has(item.id);
    return (
      <div
        key={item.id}
        className={`factory-product-topo-chip factory-product-topo-chip-pending${checked ? ' is-checked' : ''}`}
      >
        <label
          className="factory-product-topo-chip-check"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={checked}
            disabled={bulkBusy}
            onChange={() => togglePending(item.id)}
            aria-label="选择待审颗粒"
          />
        </label>
        <button
          type="button"
          className="factory-product-topo-chip-body"
          title={`待审 · ${stem} → ${view.productTitle}`}
          onClick={() => setParticleDetail(detail)}
        >
          <MathTextBlock text={stem || '待审'} className="text-[10px]" />
        </button>
      </div>
    );
  };

  return (
    <div className="ttq-admission-list factory-product-topo" style={{ padding: '0 0 8px', color: '#E8ECF1' }}>
      <div className="ttq-admission-summary" aria-label="本任务成品智能体拓扑">
        <div className="ttq-admission-summary-kicker">本任务 · 成品智能体贡献</div>
        <div className="ttq-admission-summary-row">
          点方块看颗粒详情 · 待审勾选多选入库 · 末尾高亮 = 待审
        </div>
        <div className="ttq-admission-summary-product">
          {(resolveBusy || jobProgress || busyId === 'resolve') ? (
            <Loader2 size={12} className="animate-spin shrink-0" aria-hidden />
          ) : null}
          <span className="tabular-nums">
            新建 {productStats.created} · 加入 {productStats.joined} · 待审 {critical.length}
            {' · '}
            成品智能体 {productStats.productCount}
          </span>
          {needsResolve ? (
            <button
              type="button"
              disabled={busyId === 'resolve' || resolveBusy}
              onClick={() => void onResolve()}
              className="ttq-admission-summary-link"
              style={{ textDecoration: 'none', color: '#2EC4B6' }}
            >
              {busyId === 'resolve' || resolveBusy ? '写入中…' : '整组入仓'}
            </button>
          ) : null}
          <Link to="/factory/goods" className="ttq-admission-summary-link">
            全局列表
          </Link>
        </div>
        <div className="factory-product-topo-qc">
          <span title="任务货架质检状态（抽检/下线），不是成品智能体成员是否已入组">
            本任务货架{' '}
            {FACTORY_WAREHOUSE_STATUS_LABEL[batch?.warehouseStatus || 'none']
              || batch?.warehouseStatus
              || '—'}
          </span>
          <span className="factory-product-topo-qc-actions">
            {[
              { status: 'in_qc', label: '抽检' },
              { status: 'ready', label: '可下线' },
              { status: 'offline', label: '下线' },
            ].map((opt) => (
              <button
                key={opt.status}
                type="button"
                disabled={busyId === 'warehouse'}
                onClick={() => void onWarehouse(opt.status)}
                title="本任务整批货架状态"
              >
                {opt.label}
              </button>
            ))}
          </span>
        </div>
      </div>

      {!booted && !rows.length ? (
        <p className="compare-empty" style={{ padding: 16 }}>加载成品智能体拓扑…</p>
      ) : null}

      {!rows.length && booted ? (
        <p className="compare-empty" style={{ padding: 16, color: '#E8ECF1' }}>
          尚无推荐组。请先完成「品题推荐」。
        </p>
      ) : null}

      {booted && rows.length > 0 && critical.length === 0 && orphanCritical.length === 0 ? (
        <p className="compare-empty" style={{ padding: 16, color: '#E8ECF1' }}>
          本任务成品智能体贡献已完成。可在
          <Link to="/factory/goods" className="ttq-admission-summary-link" style={{ margin: '0 4px' }}>
            全局列表
          </Link>
          查看成品智能体。
        </p>
      ) : null}

      <div className="factory-product-topo-canvas">
        {rows.map((row: FactoryWarehouseTtqa) => {
          const groupPending = criticalByGroupId.get(row.id) || [];
          // 贡献视图：无待审且已有 disposition → 本组贡献完成，隐藏大方块
          if (groupPending.length === 0 && row.factoryMeta?.disposition) return null;

          const n = row.particles?.length || 0;
          const rawTitle = String(row.title || '').trim();
          const titleLooksLikeStem =
            !rawTitle
            || /^【\d+】/.test(rawTitle)
            || (/=/.test(rawTitle) && /[÷×+\-−*/\d]/.test(rawTitle));
          const displayTitle = titleLooksLikeStem ? `推荐组 · ${n || 1} 题` : rawTitle;
          const disposition = row.factoryMeta?.disposition;
          const productId = String(
            row.factoryMeta?.productId
            || groupPending.find((it) => it.productId)?.productId
            || '',
          ).trim() || null;
          const linked = productId ? productsById.get(productId) : undefined;
          const pendingIds = new Set(groupPending.flatMap((item) => criticalParticleIds(item)));
          const settledParticles = (row.particles || []).filter(
            (p) => !pendingIds.has(String(p.particleId || '').trim()),
          );

          return (
            <section key={row.id} className="factory-product-topo-group" aria-label="推荐组">
              <header className="factory-product-topo-group-head">
                <div className="min-w-0 flex-1">
                  <MathTextBlock text={displayTitle} className="text-xs font-semibold" />
                  <div className="factory-product-topo-group-meta">
                    {dispositionShort(disposition)}
                    {linked ? ` → ${linked.title}` : ''}
                    {linked ? ` · ${linked.memberCount}/${linked.memberCap}` : ''}
                    {groupPending.length > 0 ? ` · 待审 ${groupPending.length}` : ''}
                  </div>
                </div>
                {renderGroupToolbar({
                  pendingItems: groupPending,
                  productId,
                  memberCap: linked?.memberCap || row.factoryMeta?.memberCap,
                  capBusy: Boolean(productId && busyId === productId),
                })}
              </header>
              <div className="factory-product-topo-particles">
                {settledParticles.map((p) => {
                  const fromUnit = lookupExtractQa(p.particleId, structuralUnits);
                  const qa = resolveAdmissionParticleQa({
                    particleQuestions: p.particleQuestions,
                    particleTitle: p.particleTitle,
                    subQuestions: p.subQuestions,
                    subAnswers: p.subAnswers,
                    extractQuestion: fromUnit?.question,
                    extractSubQuestions: fromUnit?.subQuestions,
                    pap: p.pap,
                  });
                  const stem = truncateMathAwarePreview(qa.question || p.particleId || '', 36);
                  return (
                    <button
                      key={p.id || p.particleId}
                      type="button"
                      className="factory-product-topo-chip"
                      title={stem}
                      onClick={() => setParticleDetail(detailFromWarehouseParticle(displayTitle, p, structuralUnits))}
                    >
                      <MathTextBlock text={stem || '颗粒'} className="text-[10px]" />
                    </button>
                  );
                })}
                {groupPending.map((item) => renderPendingChip(item))}
                {!settledParticles.length && !groupPending.length ? (
                  <span className="text-[11px]" style={{ color: '#8B94A3' }}>无颗粒</span>
                ) : null}
              </div>
            </section>
          );
        })}
      </div>

      {orphanCritical.length > 0 ? (
        <section className="factory-product-topo-pending" aria-label="未匹配推荐组的待审">
          <div className="factory-product-topo-pending-head">
            <span>
              未挂上推荐组的待审
              <span className="tabular-nums"> {orphanCritical.length}</span>
            </span>
            {renderGroupToolbar({ pendingItems: orphanCritical })}
          </div>
          <div className="factory-product-topo-particles factory-product-topo-particles-out">
            {orphanCritical.map((item) => renderPendingChip(item))}
          </div>
        </section>
      ) : null}

      {particleDetail ? (
        <ParticleDetailDialog
          detail={particleDetail}
          onClose={() => setParticleDetail(null)}
        />
      ) : null}
    </div>
  );
}
